<div class="daisy-dropdown daisy-dropdown-end relative">
  <!-- Bell Button -->
  <button
    type="button"
    tabindex="0"
    (click)="toggleDropdown()"
    class="daisy-btn daisy-btn-ghost daisy-btn-circle daisy-btn-sm relative text-base-content/80 hover:text-base-content"
    aria-label="View notifications"
  >
    <div class="daisy-indicator">
      <i class="fa-solid fa-bell text-base"></i>
      <ng-container *ngIf="unreadCount$ | async as count">
        <span
          *ngIf="count > 0"
          class="daisy-badge daisy-badge-xs daisy-badge-primary daisy-indicator-item font-semibold px-1 py-0.5"
        >
          {{ count > 99 ? '99+' : count }}
        </span>
      </ng-container>
    </div>
  </button>

  <!-- Dropdown Content -->
  <div
    tabindex="0"
    class="daisy-dropdown-content z-50 daisy-card daisy-card-compact bg-base-100 text-base-content w-80 sm:w-96 shadow-2xl border border-base-200 rounded-2xl mt-2 p-0 overflow-hidden"
  >
    <!-- Header -->
    <div class="px-4 py-3 border-b border-base-200 flex items-center justify-between bg-base-100/80 backdrop-blur-sm">
      <div class="flex items-center gap-2">
        <span class="font-semibold text-sm text-base-content">Notifications</span>
        <ng-container *ngIf="unreadCount$ | async as count">
          <span
            *ngIf="count > 0"
            class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold"
          >
            {{ count }} new
          </span>
        </ng-container>
      </div>

      <ng-container *ngIf="unreadCount$ | async as count">
        <button
          *ngIf="count > 0"
          type="button"
          (click)="markAllAsRead($event)"
          class="daisy-btn daisy-btn-ghost daisy-btn-xs text-primary hover:bg-primary/10 gap-1 font-medium"
        >
          <i class="fa-solid fa-check-double text-xs"></i>
          <span>Mark all read</span>
        </button>
      </ng-container>
    </div>

    <!-- Push Notification Permission Banner -->
    <div
      *ngIf="showPushPrompt"
      class="mx-3 my-2 p-2.5 rounded-xl bg-primary/10 border border-primary/20 flex items-start gap-2.5 text-xs"
    >
      <div
        class="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 mt-0.5"
      >
        <i class="fa-solid fa-bell text-xs"></i>
      </div>
      <div class="flex-1 min-w-0">
        <p class="font-medium text-base-content text-xs leading-snug">
          Enable push notifications
        </p>
        <p class="text-[11px] text-base-content/70 mt-0.5 leading-relaxed">
          Stay updated on classes, payments, and announcements.
        </p>
        <div class="flex items-center gap-2 mt-2">
          <button
            type="button"
            (click)="enablePushNotifications()"
            [disabled]="isRegisteringPush"
            class="daisy-btn daisy-btn-primary daisy-btn-xs font-medium"
          >
            <span
              *ngIf="isRegisteringPush"
              class="daisy-loading daisy-loading-spinner daisy-loading-xs"
            ></span>
            Enable
          </button>
          <button
            type="button"
            (click)="dismissPushPrompt()"
            class="daisy-btn daisy-btn-ghost daisy-btn-xs text-base-content/60 hover:text-base-content font-medium"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        (click)="dismissPushPrompt()"
        class="text-base-content/40 hover:text-base-content text-xs p-1"
        aria-label="Dismiss"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <!-- Feed / Body -->
    <div class="max-h-80 overflow-y-auto">
      <ng-container *ngIf="isLoading$ | async; else contentTpl">
        <div class="flex justify-center items-center p-8">
          <span class="daisy-loading daisy-loading-spinner daisy-loading-sm text-primary"></span>
        </div>
      </ng-container>

      <ng-template #contentTpl>
        <ng-container *ngIf="notifications$ | async as notifications">
          <ng-container *ngIf="notifications.length > 0; else emptyTpl">
            <div class="divide-y divide-base-200">
              <div
                *ngFor="let item of notifications"
                (click)="onNotificationClick(item)"
                class="group p-3.5 hover:bg-base-200/60 cursor-pointer transition-colors flex items-start gap-3 text-left"
                [class.bg-base-200/25]="!item.isRead"
              >
                <!-- Icon Avatar -->
                <div
                  class="w-8 h-8 rounded-full bg-base-200 flex items-center justify-center shrink-0 mt-0.5"
                >
                  <i [class]="getIconClass(item)" class="text-sm"></i>
                </div>

                <!-- Text Content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between gap-1 mb-0.5">
                    <p
                      class="text-xs font-semibold text-base-content truncate"
                      [class.text-primary]="!item.isRead"
                    >
                      {{ item.title }}
                    </p>
                    <span class="text-[10px] text-base-content/50 shrink-0">
                      {{ formatTime(item.createdAt) }}
                    </span>
                  </div>
                  <p class="text-xs text-base-content/70 line-clamp-2 leading-relaxed">
                    {{ item.message }}
                  </p>
                </div>

                <!-- Actions & Indicators -->
                <div class="flex items-center gap-1 shrink-0 mt-1">
                  <div
                    *ngIf="!item.isRead"
                    class="w-2 h-2 rounded-full bg-primary"
                    title="Unread"
                  ></div>
                  <button
                    type="button"
                    (click)="deleteNotification(item, $event)"
                    class="daisy-btn daisy-btn-ghost daisy-btn-xs text-base-content/40 hover:text-error p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete notification"
                    aria-label="Delete notification"
                  >
                    <i class="fa-solid fa-trash-can text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </ng-container>
        </ng-container>
      </ng-template>

      <!-- Empty State -->
      <ng-template #emptyTpl>
        <div class="p-8 text-center text-base-content/60">
          <div
            class="w-11 h-11 rounded-full bg-base-200/80 flex items-center justify-center mx-auto mb-2 text-base-content/40"
          >
            <i class="fa-solid fa-bell-slash text-base"></i>
          </div>
          <p class="font-medium text-xs text-base-content/80">No notifications yet</p>
          <p class="text-[11px] text-base-content/50 mt-0.5">
            You'll be notified when there's an update
          </p>
        </div>
      </ng-template>
    </div>
  </div>
</div>
